import { Plate } from '../components/Card';
import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../api';

const fmt = v => v == null ? '—' : new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 0,
}).format(v);

const fmtShort = v => {
  if (v == null) return '—';
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + ' М ₽';
  if (v >= 1_000)     return (v / 1_000).toFixed(0) + ' К ₽';
  return Math.round(v) + ' ₽';
};

function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg3)', borderRadius: 14, padding: '16px 18px 18px',
      flex: 1, minWidth: 180,
    }}>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>{icon} {label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function BranchCard({ reg, isFirst }) {
  const hasBalance = reg.balance != null;
  return (
    <div style={{
      background: 'var(--bg2)', borderRadius: 12,
      border: `1px solid ${hasBalance ? '#00a7dc35' : 'var(--border)'}`,
      padding: '14px 16px',
      opacity: hasBalance ? 1 : 0.7,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.3 }}>
            {reg.name}
          </div>
          {reg.mall && (
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{reg.mall}</div>
          )}
          {reg.address && (
            <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2 }}>📍 {reg.address}</div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {hasBalance ? (
            <div style={{ fontSize: 20, fontWeight: 800, color: '#00a7dc' }}>
              {fmtShort(reg.balance)}
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>входит в</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#00a7dc55' }}>
                {fmtShort(reg.org_balance)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text2)' }}>общий ИП</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Cash() {
  const [data, setData]       = useState(null);
  const [shifts, setShifts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('retail');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/cash/balances').then(r => r.data).catch(() => null),
      api.get('/cash/shifts?days=14').then(r => r.data).catch(() => []),
    ]).then(([bal, sh]) => {
      setData(bal);
      setShifts(sh || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Только МОИ магазины — без ФДМ
  const ownStores    = (data?.registers || []).filter(r => r.store_type === 'own');
  const academyReg   = (data?.registers || []).find(r => r.store_type === 'academy');
  const academyOk    = academyReg?.balance != null;

  // Суммируем уникальные ИП-балансы (не дублируем)
  const ownTotal     = data?.total_own || 0;
  const academyTotal = (data?.total_findirector ?? data?.total_academy) || 0;
  const grandTotal   = ownTotal + academyTotal;

  // Группируем филиалы по ИП (org_name)
  const byOrg = ownStores.reduce((acc, s) => {
    const key = s.org_name || s.name;
    if (!acc[key]) acc[key] = { org: key, balance: null, stores: [] };
    if (s.balance != null) { acc[key].balance = (acc[key].balance || 0) + s.balance; }
    acc[key].stores.push(s);
    return acc;
  }, {});
  const orgGroups = Object.values(byOrg).sort((a, b) => (b.balance || 0) - (a.balance || 0));

  // Bar chart: per-store balances
  const barData = ownStores
    .filter(s => s.balance != null && s.balance > 0)
    .sort((a, b) => b.balance - a.balance)
    .map(s => ({
      name: (s.mall || s.name).slice(0, 22),
      value: s.balance,
    }));

  // Смены только своих магазинов
  const ownShifts = shifts.filter(s => s.store_type === 'own');

  const tabs = [
    { id: 'retail',  label: `🏪 Розница (${ownStores.length} точек)` },
    { id: 'shifts',  label: `🔄 Смены (${ownShifts.length})` },
  ];

  return (
    <div>
      <div className="kp-hero">
        <Plate>Финансы · касса</Plate>
        <h1 style={{ margin: 0 }}>Наличные деньги</h1>
        <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
          Остатки наличных в кассах розничных точек ИП Бобров Илья + Финдиректор
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text2)' }}>⏳ Загружаем данные...</div>
      ) : (
        <>
          {/* KPI — только свои деньги */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            <KpiCard
              label="Итого наличных"
              value={fmt(grandTotal)}
              icon="💵"
              color="#0e9f6e"
              sub="Розница + Финдиректор (мои)"
            />
            <KpiCard
              label="Розница (МойСклад)"
              value={fmt(ownTotal)}
              icon="🏪"
              color="#00a7dc"
              sub={`${ownStores.length} точек · ${orgGroups.length} касс`}
            />
            <KpiCard
              label="Финдиректор (1С)"
              value={academyOk ? fmt(academyTotal) : 'Сервер офлайн'}
              icon="🔑"
              color={academyOk ? '#00a7dc' : 'var(--text2)'}
              sub={academyOk ? 'Остаток по 1С:УТ' : 'Данные появятся когда включат ПК с 1С'}
            />
          </div>

          {/* Разбивка по кассам */}
          <div style={{ display: 'grid', gridTemplateColumns: barData.length ? '1fr 320px' : '1fr', gap: 20, marginBottom: 24 }}>
            <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>
                💰 Сводка по кассам розницы
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>
                Источник: МойСклад · касса ИП · сумма по всем точкам каждого ИП
              </div>

              {/* Таблица по организациям */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {orgGroups.map((g, i) => (
                  <div key={g.org} style={{
                    background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px',
                    border: `1px solid ${g.balance != null ? '#00a7dc30' : 'var(--border)'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{g.org}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                          {g.stores.length} {g.stores.length === 1 ? 'точка' : g.stores.length < 5 ? 'точки' : 'точек'}
                          {g.stores.length > 1 && (
                            <span style={{ marginLeft: 8 }}>
                              {g.stores.map(s => s.mall || s.name.split(' ')[0]).filter(Boolean).slice(0, 4).join(' · ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {g.balance != null ? (
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#00a7dc' }}>
                            {fmt(g.balance)}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>нет данных</div>
                        )}
                      </div>
                    </div>

                    {/* Mini-list with per-store balances */}
                    {g.stores.length > 1 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {g.stores.map(s => (
                          <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: 'var(--text2)' }}>
                              {s.mall || s.name}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: s.balance != null ? '#00a7dc' : 'var(--text2)' }}>
                              {s.balance != null ? fmtShort(s.balance) : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Строка Финдиректор */}
                <div style={{
                  background: academyOk ? '#00a7dc18' : 'var(--bg3)',
                  borderRadius: 10, padding: '12px 16px',
                  border: `1px solid ${academyOk ? '#00a7dc40' : 'var(--border)'}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: academyOk ? '#00a7dc' : 'var(--text2)' }}>
                        Финдиректор
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                        {academyOk ? 'Данные из 1С:УТ 11.5' : '1С сервер выключен — данные недоступны'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {academyOk ? (
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#00a7dc' }}>{fmt(academyTotal)}</div>
                      ) : (
                        <div style={{ fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>офлайн</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Итог */}
                <div style={{
                  background: grandTotal > 0 ? 'rgba(14,159,110,0.08)' : 'var(--bg3)',
                  borderRadius: 10, padding: '12px 16px',
                  border: `1px solid rgba(14,159,110,0.25)`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0e9f6e' }}>
                    Итого (только мои деньги)
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#0e9f6e' }}>
                    {fmt(grandTotal)}
                  </div>
                </div>
              </div>
            </div>

            {/* Гистограмма */}
            {barData.length > 0 && (
              <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>
                  По кассам
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 10, fill: 'var(--text2)' }} />
                    <YAxis type="category" dataKey="name" width={130}
                      tick={{ fontSize: 10, fill: 'var(--text2)' }}
                      tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      formatter={v => [fmt(v), 'Остаток']} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {barData.map((_, i) => <Cell key={i} fill="#00a7dc" fillOpacity={0.9 - i * 0.08} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Вкладки */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '8px 16px', background: 'transparent', border: 'none',
                borderBottom: tab === t.id ? '2px solid #00a7dc' : '2px solid transparent',
                color: tab === t.id ? '#00a7dc' : 'var(--text2)',
                cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 700 : 400, marginBottom: -1,
              }}>{t.label}</button>
            ))}
          </div>

          {/* РОЗНИЦА — все точки */}
          {tab === 'retail' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                Остатки наличных в кассах · источник: МойСклад · ФДМ партнёры исключены
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {ownStores.map(r => (
                  <BranchCard key={r.key} reg={r} />
                ))}
              </div>
            </div>
          )}

          {/* СМЕНЫ */}
          {tab === 'shifts' && (
            <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
                🔄 Кассовые смены — моя розница (14 дней)
              </div>
              {ownShifts.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Нет данных о сменах</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg3)' }}>
                        {['Магазин', 'Открытие', 'Закрытие', 'Статус', 'Нал. принято', 'Нал. начало', 'Нал. конец'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ownShifts.slice(0, 50).map((s, i) => {
                        const isOpen = s.status === 'open';
                        return (
                          <tr key={i} style={{ borderTop: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                            <td style={{ padding: '9px 14px', fontWeight: 600, color: 'var(--text)' }}>
                              {s.store_name || '—'}
                            </td>
                            <td style={{ padding: '9px 14px', color: 'var(--text2)' }}>
                              {s.opened_at ? s.opened_at.slice(0, 16).replace('T', ' ') : '—'}
                            </td>
                            <td style={{ padding: '9px 14px', color: 'var(--text2)' }}>
                              {s.closed_at ? s.closed_at.slice(0, 16).replace('T', ' ') : '—'}
                            </td>
                            <td style={{ padding: '9px 14px' }}>
                              <span style={{
                                background: isOpen ? '#0e9f6e20' : 'var(--bg3)',
                                color: isOpen ? '#0e9f6e' : 'var(--text2)',
                                borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                              }}>{isOpen ? '🟢 Открыта' : '⬜ Закрыта'}</span>
                            </td>
                            <td style={{ padding: '9px 14px', fontWeight: 700, color: '#0e9f6e' }}>
                              {fmt(s.received_cash)}
                            </td>
                            <td style={{ padding: '9px 14px', color: 'var(--text1)' }}>{fmt(s.open_balance)}</td>
                            <td style={{ padding: '9px 14px', color: 'var(--text1)' }}>
                              {s.close_balance ? fmt(s.close_balance) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text2)', textAlign: 'right' }}>
            Обновлено: {data?.updated_at ? data.updated_at.replace('T', ' ').slice(0, 16) + ' UTC' : '—'}
            <button onClick={load} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 11 }}>
              🔄 Обновить
            </button>
          </div>
        </>
      )}
    </div>
  );
}
